import { createContext, useContext, useMemo } from "react";
import { useSelector } from "react-redux";
import { selectSupplierInfo } from "../../../../store/slices/supplierInfoSlice";

// Create context for section numbering
const SectionNumberingContext = createContext();

export const SectionNumberingProvider = ({ children }) => {
	const supplierInfo = useSelector(selectSupplierInfo);
	const { amounts } = supplierInfo || { amounts: {} };
	const { generalsBasis, generalsValues } = amounts || {};

	// Pre-calculate section numbering based on conditions
	const sectionNumbers = useMemo(() => {
		let currentNum = 1;
		const numbers = {};

		// Contract Summary Section
		numbers.contractAmount = currentNum++;
		numbers.confirmedChanges = currentNum++;
		numbers.currentOrderAmount = currentNum++;

		// Performance Overview Section
		numbers.totalPerformance = currentNum++;
		numbers.testResultNet = currentNum++;

		// Deductions Section
		numbers.discount = currentNum++;
		numbers.retention = currentNum++; // Security/Warranty retention

		// Corrections Section
		numbers.defectsDeduction = currentNum++;
		numbers.damage = currentNum++;
		numbers.counterclaims = currentNum++;
		numbers.provisionalRetention = currentNum++;
		numbers.penalty = currentNum++;
		numbers.correctionAmount = currentNum++;

		// Total Summary Section
		numbers.releasedInvoiceAmount = currentNum++;
		numbers.cashDiscount = currentNum++;
		numbers.subtotal = currentNum++;

		// Generals Section (if net basis)
		if (generalsBasis === "net" && generalsValues?.length > 0) {
			numbers.generals = [];
			for (let i = 0; i < generalsValues.length; i++) {
				numbers.generals.push(currentNum++);
			}
			numbers.subtotalInclDeductions = currentNum++;
		}
		numbers.vat = currentNum++;
		numbers.subtotal1 = currentNum++;

		// Generals Section (if gross basis)
		if (generalsBasis === "gross" && generalsValues?.length > 0) {
			numbers.generals = [];
			for (let i = 0; i < generalsValues.length; i++) {
				numbers.generals.push(currentNum++);
			}
			numbers.subtotalInclDeductions = currentNum++;
		}

		// Remaining Total Summary Section items (after generals if gross)
		numbers.releasedInstalmentPayments = currentNum++;
		numbers.totalGross = currentNum++;

		return numbers;
	}, [generalsBasis, generalsValues]);

	return (
		<SectionNumberingContext.Provider value={{ sectionNumbers }}>
			{children}
		</SectionNumberingContext.Provider>
	);
};

export const useSectionNumbering = () => {
	const context = useContext(SectionNumberingContext);
	if (!context) {
		throw new Error(
			"useSectionNumbering must be used within a SectionNumberingProvider",
		);
	}
	return context;
};
